import FormItem from "components/common/FormItem";
import { JOHOKU_ACCOUNT, JOHOKU_ACCOUNT_TYPE } from "enums";
import { TOOLTIP_DATA } from "enums/tooltip_data";
import { CorpBankModel, JohokuBranchMstModel } from "models";
import React from "react";
import Select from "react-select";

const bankTypes = [
  {
    value: JOHOKU_ACCOUNT_TYPE.HAS_ACCOUNT,
    name: "口座あり",
  },
  {
    value: JOHOKU_ACCOUNT_TYPE.NO_ACCOUNT,
    name: "口座なし",
  },
];

type PROPS = {
  // isJHK: boolean;
  idx: number;
  data: CorpBankModel;
  branchMst: JohokuBranchMstModel[];
  onChange: () => void;
  onRefreshBankAcc: (isJHK: boolean) => void;
  disabled?: boolean;
};

const AccountForms: React.FC<PROPS> = (props) => {
  // const isJHK = props.isJHK;
  const idx = props.idx;
  const data = props.data;
  const disabled = props.disabled;

  const [isJHK, setIsJHK] = React.useState(false);
  const [branchValue, setBranchValue] = React.useState("");
  const [johokuAccountValue, setJohokuAccountValue] = React.useState(JOHOKU_ACCOUNT_TYPE.HAS_ACCOUNT);

  React.useEffect(() => {
    if (isJHK) {
      setBranchValue(props.data.branch_cd);
    } else {
      setBranchValue("");
    }
  }, [props.data.branch_cd, isJHK]);


  React.useEffect(() => {
    if(!data) return;
    setJohokuAccountValue(data.johoku_account_type);
    setIsJHK(data.johoku_account_type === JOHOKU_ACCOUNT_TYPE.HAS_ACCOUNT);
  }, [data])

  const selectedBranch = React.useMemo(
    () => props.branchMst.find((i) => i.johoku_branch_cd === branchValue),
    [props.branchMst, branchValue],
  );
  const customFilter = React.useCallback((branch: any, textSearch: string) => {
    if (branch.data.johoku_branch_name.includes(textSearch) || branch.data.johoku_branch_cd.includes(textSearch)) {
      return true;
    } else {
      return false;
    }
  }, []);

  const johokuAccountType = React.useMemo(
    () => bankTypes.find((i) => i.value === johokuAccountValue),
    [johokuAccountValue],
  );

  const onUpdateJohokuAccountType = (type: JOHOKU_ACCOUNT_TYPE, is_JHK: boolean) => {
    setIsJHK(is_JHK);
    setJohokuAccountValue(type);

    data.johoku_account_type = type;
    props.onRefreshBankAcc(is_JHK);
  }

  return (
    <>
      <FormItem label="城北信用金庫" required>
        <Select
          isDisabled={disabled}
          placeholder=""
          className="react-select"
          value={johokuAccountType}
          classNamePrefix="control"
          onChange={(option) => {
            let value = option ? option.value : JOHOKU_ACCOUNT_TYPE.HAS_ACCOUNT;
            let is_JHK = value === JOHOKU_ACCOUNT_TYPE.HAS_ACCOUNT;

            if (value === johokuAccountType?.value) return;
            onUpdateJohokuAccountType(value, is_JHK);
          }}
          getOptionLabel={(option) => `${option.name}`}
          getOptionValue={(option) => `${option.value}`}
          options={bankTypes}
        />
      </FormItem>
      <FormItem
        label="金融機関コード"
        required
        tooltip={TOOLTIP_DATA.CORPS.bank_cd}
      >
        <input
          className="w-full"
          disabled={disabled || isJHK}
          value={data.bank_cd}
          onBlur={(e) => {
            const numericValue = e.target.value.replace(/[^,\d]/g, "");
            const validNumericValue = numericValue;
            if (validNumericValue) {
              const numericString = validNumericValue.replace(/,/g, "");
              data.bank_cd = numericString;
            } else {
              data.bank_cd = "";
            }
            props.onChange();
          }}
          onChange={(e) => {
            const numericValue = e.target.value;
            data.bank_cd = numericValue;
            const tmpIsJHK = numericValue === JOHOKU_ACCOUNT.bank_cd;
            if (tmpIsJHK) {
              onUpdateJohokuAccountType(
                JOHOKU_ACCOUNT_TYPE.HAS_ACCOUNT,
                tmpIsJHK
              );
            }
            props.onChange();
          }}
          type="tel"
        />
      </FormItem>
      <FormItem label="金融機関名" required>
        <input
          className="w-full"
          disabled={disabled || isJHK}
          value={data.bank_name}
          onChange={(e) => {
            data.bank_name = e.target.value;
            props.onChange();
          }}
        />
      </FormItem>
      {isJHK ? (
        <FormItem label="城北信用金庫支店" required>
          <Select
            isClearable
            isSearchable
            isDisabled={disabled}
            placeholder=""
            className="react-select"
            classNamePrefix="control"
            value={selectedBranch}
            filterOption={customFilter}
            noOptionsMessage={() => <></>}
            onChange={(option) => {
              setBranchValue(option ? option.johoku_branch_cd : "");
              data.branch_cd = option ? option.johoku_branch_cd : "";
              data.branch_name = option ? option.johoku_branch_name : "";
            }}
            getOptionLabel={(option) =>
              `${option.johoku_branch_cd}：${option.johoku_branch_name}`
            }
            getOptionValue={(option) => option.johoku_branch_cd}
            options={props.branchMst}
          />
        </FormItem>
      ) : (
        <>
          <FormItem
            label="支店コード"
            required
            tooltip={TOOLTIP_DATA.CORPS.branch_cd}
          >
            <input
              className="w-full"
              disabled={disabled}
              value={data.branch_cd}
              onBlur={(e) => {
                const numericValue = e.target.value.replace(/[^,\d]/g, "");
                const validNumericValue = numericValue.match(/[\d,]+/g);
                if (validNumericValue) {
                  const numericString = validNumericValue[0].replace(/,/g, "");
                  data.branch_cd = numericString;
                } else {
                  data.branch_cd = "";
                }
                props.onChange();
              }}
              onChange={(e) => {
                const numericValue = e.target.value;
                data.branch_cd = numericValue;
                props.onChange();
              }}
              type="tel"
            />
          </FormItem>
          <FormItem label="支店名" required>
            <input
              className="w-full"
              disabled={disabled}
              value={data.branch_name}
              onChange={(e) => {
                data.branch_name = e.target.value;
                props.onChange();
              }}
            />
          </FormItem>
        </>
      )}
      <FormItem label="預金種別" required>
        <div className="flex">
          <label className="cursor-pointer">
            <input
              type="radio"
              name={`${idx}_account_type`}
              checked={data.account_type === 1}
              disabled={disabled}
              onChange={(e) => {
                data.account_type = 1;
                props.onChange();
              }}
            />
            <span className="ml-2">普通</span>
          </label>
          <label className="cursor-pointer ml-8">
            <input
              type="radio"
              name={`${idx}_account_type`}
              checked={data.account_type === 2}
              disabled={disabled}
              onChange={(e) => {
                data.account_type = 2;
                props.onChange();
              }}
            />
            <span className="ml-2">当座</span>
          </label>
        </div>
      </FormItem>
      <FormItem
        label="口座番号"
        required
        tooltip={TOOLTIP_DATA.CORPS.account_no}
      >
        <input
          className="w-full"
          disabled={disabled}
          value={data.account_no}
          onChange={(e) => {
            data.account_no = e.target.value;
            props.onChange();
          }}
          type="tel"
        />
      </FormItem>
      <FormItem
        label="口座名義（半角ｶﾅ）"
        required
        tooltip={TOOLTIP_DATA.CORPS.account_name}
      >
        <input
          className="w-full"
          disabled={disabled}
          value={data.account_name}
          onChange={(e) => {
            data.account_name = e.target.value.replaceAll("ｰ", "-");
            props.onChange();
          }}
        />
      </FormItem>
    </>
  );
};

export default AccountForms;
